'use client';

import React from 'react';
import NextLink, { type LinkProps as NextLinkProps } from 'next/link';
import { cn } from 'lib/utils';
import { trackEvent, type SegmentEventProperties } from 'lib/analytics';

// Tracking Types
type TrackingProps = {
  trackingProps?: {
    event: string;
    properties: SegmentEventProperties;
  };
};

// Original Button Types
type CommonProps = {
  children: React.ReactNode;
  className?: string;
  textSize?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'tertiary';
};

type AnchorOnlyProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'href' | 'onClick' | 'className'
>;

type ButtonOnlyProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'type' | 'className'
> & {
  type?: 'button' | 'submit' | 'reset';
};

type LinkVariantProps = CommonProps &
  AnchorOnlyProps &
  Omit<NextLinkProps, 'href'> & {
    href: NextLinkProps['href'];
    onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  } & TrackingProps;

type ButtonVariantProps = CommonProps &
  ButtonOnlyProps & {
    href?: never;
    disabled?: boolean;
  } & TrackingProps;

export type ButtonProps = ButtonVariantProps | LinkVariantProps;

const Button = ({
  children,
  className: classNameProp,
  textSize = 'md',
  variant = 'primary',
  trackingProps,
  ...rest
}: ButtonProps) => {
  const className = cn(
    'font-sans font-bold inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3.5 no-underline focus-visible:outline-2 focus-visible:outline-offset-5 focus-visible:outline-athletic-500 transition-all [&>svg]:transition-colors cursor-pointer disabled:cursor-not-allowed',
    {
      // Variant Styles
      'bg-athletic-500 text-clay-50 hover:not-disabled:bg-blue-500 [&>svg]:fill-white disabled:bg-neutral-200 disabled:text-clay-50 disabled:[&>svg]:fill-clay-50':
        variant === 'primary',
      'bg-clay-50 text-stone-800 hover:not-disabled:bg-neutral-500 [&>svg]:fill-stone-800 disabled:bg-neutral-200 disabled:text-clay-50 disabled:[&>svg]:fill-clay-50':
        variant === 'secondary',
      'bg-transparent text-clay-50 border-1 border-clay-50/30 hover:not-disabled:border-white [&>svg]:fill-white disabled:opacity-50 disabled:bg-neutral-200/20 disabled:text-clay-50 disabled:[&>svg]:fill-clay-50':
        variant === 'tertiary',

      // Text Size Styles
      'text-p3/5': textSize === 'sm',
      'text-p2/5': textSize === 'md',
      'text-p1/5': textSize === 'lg',
    },
    classNameProp
  );

  if ('href' in rest) {
    const linkProps = rest as LinkVariantProps;
    const { onClick: originalOnClick } = linkProps;

    const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      // 1. Fire Segment event if trackingProps are provided
      if (trackingProps && trackingProps.event) {
        trackEvent(trackingProps.event, trackingProps.properties);
      }
      // 2. Execute any original onClick handler
      if (originalOnClick) {
        originalOnClick(e);
      }
    };

    return (
      <NextLink {...{ className }} {...linkProps} onClick={handleLinkClick}>
        {children}
      </NextLink>
    );
  }

  const {
    onClick: originalOnClick,
    type = 'button',
    disabled,
    ...buttonProps
  } = rest as ButtonVariantProps;

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // 1. Fire Segment event if trackingProps are provided
    if (trackingProps && trackingProps.event) {
      trackEvent(trackingProps.event, trackingProps.properties);
    }
    // 2. Execute any original onClick handler
    if (originalOnClick) {
      originalOnClick(e);
    }
  };

  return (
    <button
      {...{ className, type, disabled }}
      onClick={handleButtonClick}
      {...buttonProps}
    >
      {children}
    </button>
  );
};

export default Button;
